import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { subscriptionsApi } from '../services/api'
import { Card, Badge, PageLoader } from '../components/ui'
import { 
  CreditCard, 
  Check, 
  Clock, 
  AlertTriangle, 
  ExternalLink, 
  Loader2, 
  Gift,
  Users,
  Baby,
  UserCheck,
  Database,
  Sparkles,
  Info,
  RefreshCw,
  AlertCircle,
  X,
  FileText,
  CloudCog,
  FileBarChart
} from 'lucide-react'

// Componente Progress Bar per limiti
const LimitProgress = ({ icon: Icon, label, current, max, color = 'primary' }) => {
  const isUnlimited = max === 'Illimitati' || max >= 999999
  const percentage = isUnlimited ? 0 : Math.min((current / max) * 100, 100)
  const isWarning = !isUnlimited && percentage >= 80
  const isCritical = !isUnlimited && percentage >= 95

  const bgColor = isCritical ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : `bg-${color}-500`

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <div className={`p-2 rounded-lg ${isCritical ? 'bg-red-100' : isWarning ? 'bg-yellow-100' : 'bg-primary-100'}`}>
        <Icon className={`h-5 w-5 ${isCritical ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-primary-600'}`} />
      </div>
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {current} / {isUnlimited ? '∞' : max}
          </span>
        </div>
        {!isUnlimited && (
          <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
            <div 
              className={`h-full ${bgColor} rounded-full transition-all duration-500`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        )}
        {isUnlimited && (
          <div className="h-2 bg-green-200 dark:bg-green-700 rounded-full">
            <div className="h-full bg-green-500 rounded-full w-full opacity-50" />
          </div>
        )}
      </div>
    </div>
  )
}

// Helper per formattare i limiti
const formatLimit = (value) => {
  if (value >= 999999 || value === 'Illimitati' || value === 'Illimitato') {
    return 'Illimitati'
  }
  return value
}

// Componente Card Piano con dati dinamici dal backend
// FIX: Usa betaMode da SystemSettings invece di tenant.isBetaTester
const PlanCard = ({ planId, plan, isCurrent, isTrial, onUpgrade, upgrading, betaMode }) => {
  const limits = plan.limits || {}
  const features = plan.features || {}
  const minori = limits.maxMinori || limits.minori || 0
  const educatori = limits.maxEducatori || limits.educatori || 0
  const coordinatori = limits.maxCoordinatori || limits.coordinatori || 0
  const storico = limits.storageMonths || limits.storico || 0

  // Features per funzionalità incluse
  const hasBackupGDrive = features.backupGoogleDrive !== false
  const hasReportBase = features.reportsBasic !== false
  const hasReportAdvanced = features.reportsAdvanced === true

  return (
    <Card className={`relative ${isCurrent ? 'border-primary-500 ring-2 ring-primary-500' : ''}`}>
      {isCurrent && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2" variant="primary">
          Attuale
        </Badge>
      )}
      {plan.recommended && (
        <Badge className="absolute -top-3 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          Consigliato
        </Badge>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          {plan.name || plan.displayName}
        </h3>
        
        {/* Prezzo - blur solo se betaMode è attivo */}
        <div className="mt-3 relative">
          <p className={betaMode ? "blur-lg select-none pointer-events-none" : ""}>
            <span className="text-3xl font-bold">
              {plan.pricing?.monthly?.formatted ? plan.pricing.monthly.formatted : `€${plan.price || '??'}`}
            </span>
            <span className="text-gray-500">/mese</span>
          </p>
        </div>
        
        {/* Badge Beta Tester - solo se betaMode è attivo */}
        {betaMode && (
          <div className="mt-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-full text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            🎉 Sei un Beta Tester! Accesso gratuito
          </div>
        )}
      </div>

      {/* Limiti Piano - DINAMICI DAL BACKEND */}
      <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
        <p className="text-xs font-medium text-primary-700 dark:text-primary-300 mb-2">Limiti inclusi:</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1">
            <Baby className="w-3 h-3 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {formatLimit(minori)} minori
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {formatLimit(educatori)} educatori
            </span>
          </div>
          <div className="flex items-center gap-1">
            <UserCheck className="w-3 h-3 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {formatLimit(coordinatori)} coord.
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Database className="w-3 h-3 text-primary-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {storico >= 999 ? 'Illimitato' : `${storico} mesi`}
            </span>
          </div>
        </div>
      </div>

      {/* Funzionalità incluse - NUOVO */}
      <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Funzionalità incluse:</p>
        <div className="space-y-1.5 text-sm">
          <div className={`flex items-center gap-1.5 ${hasBackupGDrive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {hasBackupGDrive ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-gray-400" />}
            <CloudCog className="w-3 h-3" />
            <span>Backup Google Drive</span>
          </div>
          <div className={`flex items-center gap-1.5 ${hasReportBase ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {hasReportBase ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-gray-400" />}
            <FileBarChart className="w-3 h-3" />
            <span>Report Base</span>
          </div>
          <div className={`flex items-center gap-1.5 ${hasReportAdvanced ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {hasReportAdvanced ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-gray-400" />}
            <FileBarChart className="w-3 h-3" />
            <span>Report Avanzati</span>
          </div>
        </div>
      </div>

      {/* Personalizzazione Form */}
      <div className="mb-6 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
        <p className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Personalizzazione Form:
        </p>
        <div className="space-y-1.5 text-sm">
          {planId === 'starter' ? (
            <>
              <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                <X className="w-3 h-3 text-gray-400" />
                <span>Personalizza opzioni campi</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                <X className="w-3 h-3 text-gray-400" />
                <span>Sezioni personalizzate</span>
              </div>
            </>
          ) : planId === 'professional' ? (
            <>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Personalizza opzioni (max 5)</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Disabilita campi illimitati</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                <X className="w-3 h-3 text-gray-400" />
                <span>Sezioni personalizzate</span>
              </div>
            </>
          ) : planId === 'enterprise' ? (
            <>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Personalizza opzioni (max 5)</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Disabilita campi illimitati</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>3 sezioni personalizzate (max 5 elem.)</span>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Button - disabilitato per coordinatore, gestito dal Responsabile */}
      <div className="w-full py-2.5 rounded-lg font-medium text-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm">
        {isCurrent ? '✓ Piano Attuale' : 'Gestito dal Responsabile'}
      </div>
    </Card>
  )
}

// Componente errore con retry
const ErrorState = ({ onRetry, message }) => (
  <Card className="border-red-200 bg-red-50 dark:bg-red-900/10">
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        Dati non disponibili
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        {message || 'Impossibile caricare i dati. Verifica la connessione e riprova.'}
      </p>
      <button 
        onClick={onRetry}
        className="btn-primary flex items-center gap-2"
      >
        <RefreshCw className="h-4 w-4" />
        Ricarica
      </button>
    </div>
  </Card>
)

export default function SubscriptionPage() {
  const { tenant } = useAuth()
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [usage, setUsage] = useState(null)
  const [limits, setLimits] = useState(null)
  const [plans, setPlans] = useState(null) // null = non caricati, {} = caricati ma vuoti
  const [couponCode, setCouponCode] = useState('')
  const [applyingCoupon, setApplyingCoupon] = useState(false)
  const [upgrading, setUpgrading] = useState(null)
  // FIX: Aggiunto state per betaMode da SystemSettings
  const [betaMode, setBetaMode] = useState(true)

  useEffect(() => { 
    loadData() 
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Carica subscription e piani in parallelo
      const [subRes, plansRes] = await Promise.all([
        subscriptionsApi.getCurrent(),
        subscriptionsApi.getPlans()
      ])
      
      // Processa subscription
      const subData = subRes.data.data || subRes.data
      setSubscription(subData.subscription)
      
      if (subData.tenant) {
        const rawLimits = subData.tenant.limits || null
        const rawUsage = subData.tenant.usage || null

        // Normalizza nomi campi: il backend usa maxMinori/maxEducatori/maxCoordinatori
        // il componente si aspetta minori/educatori/coordinatori
        setLimits(rawLimits ? {
          minori:       rawLimits.minori       ?? rawLimits.maxMinori       ?? 10,
          educatori:    rawLimits.educatori    ?? rawLimits.maxEducatori    ?? 3,
          coordinatori: rawLimits.coordinatori ?? rawLimits.maxCoordinatori ?? 1
        } : null)

        setUsage(rawUsage ? {
          minori:       rawUsage.minori       ?? rawUsage.minoriCount       ?? 0,
          educatori:    rawUsage.educatori    ?? rawUsage.educatoriCount    ?? 0,
          coordinatori: rawUsage.coordinatori ?? rawUsage.coordinatoriCount ?? 0
        } : null)
      }
      
      // Processa piani dal backend - NESSUN FALLBACK
      if (plansRes?.data?.data?.plans && plansRes.data.data.plans.length > 0) {
        const backendPlans = {}
        plansRes.data.data.plans.forEach(p => {
          backendPlans[p.id] = {
            ...p,
            name: p.name,
            displayName: p.name,
            price: parseInt(p.pricing?.monthly?.formatted?.replace('€', '') || '0'),
            limits: p.limits,
            features: p.features || {},
            recommended: p.recommended
          }
        })
        setPlans(backendPlans)
        
        // FIX: Recupera betaMode dalla risposta API
        setBetaMode(plansRes.data.data.betaMode ?? true)
      } else {
        // Nessun piano disponibile
        setPlans({})
      }
      
    } catch (err) {
      console.error('Error loading subscription:', err)
      setError(err.message || 'Errore nel caricamento dei dati')
    } finally {
      setLoading(false)
    }
  }

  const handleUpgrade = async (planId) => {
    try {
      setUpgrading(planId)
      const res = await subscriptionsApi.createCheckout(planId)
      window.location.href = res.data.data?.url || res.data.url
    } catch (err) {
      showError(err.response?.data?.message || 'Errore')
      setUpgrading(null)
    }
  }

  const handleManage = async () => {
    try {
      const res = await subscriptionsApi.openPortal()
      window.location.href = res.data.data?.url || res.data.url
    } catch (err) {
      showError('Errore apertura portale')
    }
  }

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return
    setApplyingCoupon(true)
    try {
      const res = await subscriptionsApi.validateCoupon(couponCode)
      if (res.data.valid) {
        showSuccess(`Coupon "${couponCode}" valido! ${res.data.data?.description || ''}`)
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Coupon non valido')
    } finally {
      setApplyingCoupon(false)
    }
  }

  if (loading) return <PageLoader />

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <ErrorState onRetry={loadData} message={error} />
      </div>
    )
  }

  const currentPlan = subscription?.plan || tenant?.currentPlan || 'trial'
  const isTrial = currentPlan === 'trial' || tenant?.status === 'trial'
  const trialDaysLeft = tenant?.trialDaysRemaining || 0
  const hasPlans = plans && Object.keys(plans).length > 0

  // Limiti correnti (con fallback)
  const currentLimits = limits || {
    minori: tenant?.limits?.maxMinori || 10,
    educatori: tenant?.limits?.maxEducatori || 3,
    coordinatori: tenant?.limits?.maxCoordinatori || 1
  }

  const currentUsage = usage || {
    minori: 0,
    educatori: 0,
    coordinatori: 0
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Abbonamento</h1>
          <p className="text-gray-500">Consulta i piani disponibili</p>
        </div>
      </div>

      {/* Banner read-only */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Sezione in sola lettura</p>
          <p className="text-sm text-blue-600 dark:text-blue-400 mt-0.5">
            La gestione dell'abbonamento è riservata al Responsabile dell'organizzazione. Qui puoi consultare i piani disponibili e le funzionalità incluse.
          </p>
        </div>
      </div>

      {/* Current Plan Status */}
      <Card className={`${isTrial ? 'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/10' : 'border-green-300 bg-green-50 dark:bg-green-900/10'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {isTrial ? (
              <Clock className="h-10 w-10 text-yellow-600" />
            ) : (
              <CreditCard className="h-10 w-10 text-green-600" />
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white capitalize flex items-center gap-2">
                Piano {currentPlan}
                {betaMode && (
                  <span className="inline-flex items-center gap-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full text-xs font-medium">
                    <Sparkles className="w-3 h-3" />
                    Beta Tester
                  </span>
                )}
              </h2>
              {isTrial && (
                <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                  {trialDaysLeft > 0 ? `${trialDaysLeft} giorni rimanenti` : 'Scaduto'}
                </p>
              )}
              {!isTrial && subscription?.currentPeriodEnd && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Rinnovo: {new Date(subscription.currentPeriodEnd).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Limiti Utilizzo - Solo se disponibili */}
      {currentLimits && currentUsage ? (
        <Card>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Database className="h-5 w-5 mr-2 text-primary-600" />
            Utilizzo Attuale
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <LimitProgress 
              icon={Baby} 
              label="Minori" 
              current={currentUsage.minori} 
              max={currentLimits.minori} 
            />
            <LimitProgress 
              icon={Users} 
              label="Educatori" 
              current={currentUsage.educatori} 
              max={currentLimits.educatori} 
            />
            <LimitProgress 
              icon={UserCheck} 
              label="Coordinatori" 
              current={currentUsage.coordinatori} 
              max={currentLimits.coordinatori} 
            />
          </div>
          
          {/* Warning se vicino ai limiti */}
          {(currentUsage.minori / currentLimits.minori >= 0.8 || 
            currentUsage.educatori / currentLimits.educatori >= 0.8) && (
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Ti stai avvicinando ai limiti del piano
                </p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                  Considera l'upgrade per sbloccare più risorse
                </p>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-center py-6 text-gray-500">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>Limiti di utilizzo non disponibili</span>
            <button onClick={loadData} className="ml-3 text-primary-600 hover:text-primary-700">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </Card>
      )}

      {/* Trial Alert */}
      {isTrial && trialDaysLeft <= 7 && trialDaysLeft > 0 && (
        <Card className="border-red-300 bg-red-50 dark:bg-red-900/10">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-100">
                Il trial sta per scadere!
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300">
                Scegli un piano per continuare a usare Il Diario di Bordo senza interruzioni.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Plans Grid */}
      {hasPlans ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(plans)
            .filter(([planId]) => planId !== 'trial')
            .map(([planId, plan]) => (
              <PlanCard
                key={planId}
                planId={planId}
                plan={{ ...plan, recommended: planId === 'professional' || plan.recommended }}
                isCurrent={currentPlan === planId}
                isTrial={isTrial}
                onUpgrade={handleUpgrade}
                upgrading={upgrading}
                betaMode={betaMode}
              />
            ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-10 w-10 text-gray-400 mb-3" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Piani non disponibili al momento
            </p>
            <button 
              onClick={loadData}
              className="btn-secondary flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Ricarica
            </button>
          </div>
        </Card>
      )}

      {/* Info Footer - diverso per beta tester e utenti normali */}
      {/* FIX: Usa betaMode invece di tenant?.isBetaTester */}
      {betaMode ? (
        <Card className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border-primary-200 dark:border-primary-800">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-primary-800 dark:text-primary-200 font-medium">
                🎉 Grazie per essere un Beta Tester!
              </p>
              <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                Durante la fase beta, hai accesso completo a tutte le funzionalità gratuitamente. 
                I prezzi mostrati entreranno in vigore al termine del periodo beta. 
                Ti avviseremo con largo anticipo prima di qualsiasi modifica.
              </p>
              <p className="text-xs text-primary-600 dark:text-primary-400 mt-2">
                🔒 Pagamenti sicuri con Stripe. Puoi cancellare in qualsiasi momento.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Tutti i piani includono supporto via email e aggiornamenti gratuiti.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                🔒 Pagamenti sicuri con Stripe. Puoi cancellare in qualsiasi momento.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
